/**
 * Role and permission codes as plain constants (no Zod), so UI code can import them without pulling
 * the validation library into the browser bundle. Schemas live in `access.ts`.
 */

/** Staff roles (PRD §4). `owner` is tenant-wide; every other role is bound to one outlet. */
export const ROLES = ["owner", "outlet_manager", "cashier", "barista", "inventory_staff", "finance"] as const;
export type Role = (typeof ROLES)[number];

/**
 * Permission codes. The database (`permissions`, `role_permissions`) is the source of truth for
 * which role grants what; this list types the codes the code base checks.
 */
export const PERMISSIONS = [
  "outlets.read",
  "outlets.manage",
  "outlets.settings",
  "catalogue.manage",
  "staff.manage",
  "devices.manage",
  "sales.create",
  "sales.void",
  "refunds.approve",
  "discounts.override",
  "discounts.manage",
  "tax.manage",
  "customers.manage",
  "cash.shift.operate",
  "cash.shift.approve",
  "inventory.receive",
  "inventory.adjust",
  "inventory.count",
  "kitchen.queue",
  "reports.operational",
  "reports.financial",
  "settings.payment",
  "audit.read",
  /** Take cash for an order whose QRIS result is still unknown (R1-26, AC-37): a manager override. */
  "payments.override",
  /** Review and resolve QRIS reconciliation cases: late successes and overpayments (R1-26, AC-08). */
  "payments.reconcile",
  /**
   * Verify (or reject) manual static-QRIS payments against the merchant account (R1-28, AC-36):
   * owners, outlet managers and finance; never the cashier who only recorded them.
   */
  "payments.verify",
  /** Issue and revoke the bounded offline register leases (R1-38). */
  "offline.manage",
  /** Review quarantined offline sales and accept or reject them (R1-41, AC-39). */
  "offline.reconcile",
] as const;
export type Permission = (typeof PERMISSIONS)[number];

/** Roles that may be carried by an offline selling lease. */
export const OFFLINE_LEASE_ROLES = ["cashier"] as const;

/** The deliberately small permission envelope available while disconnected. */
export const OFFLINE_LEASE_PERMISSIONS = ["sales.create", "cash.shift.operate"] as const;

export const OFFLINE_LEASE_STATUSES = ["active", "expired", "revoked"] as const;
export type OfflineLeaseState = (typeof OFFLINE_LEASE_STATUSES)[number];

/** Audit actions (PRD §23 evidence). */
export const AUDIT_ACTIONS = [
  "auth.session.created",
  "auth.session.revoked",
  "auth.sign_in.failed",
  "mfa.totp.enabled",
  "mfa.totp.disabled",
  "mfa.recovery_code.used",
  "mfa.locked",
  "staff.created",
  "staff.updated",
  "staff.deactivated",
  "staff.reactivated",
  "staff.roles_changed",
  "device.enrolled",
  "device.revoked",
  "offline.lease_issued",
  "offline.lease_revoked",
  "offline.command_quarantined",
  "offline.exception_resolved",
  "staff.pin_set",
  "pin.locked",
  "outlet.created",
  "outlet.updated",
  "outlet.deactivated",
  "outlet.reactivated",
  "catalogue.category_created",
  "catalogue.category_updated",
  "catalogue.product_created",
  "catalogue.product_updated",
  "catalogue.variant_created",
  "catalogue.variant_updated",
  "catalogue.availability_changed",
  "catalogue.price_scheduled",
  "catalogue.image_changed",
  "catalogue.modifier_group_created",
  "catalogue.modifier_group_updated",
  "catalogue.modifier_option_created",
  "catalogue.modifier_option_updated",
  "catalogue.product_modifiers_changed",
  "catalogue.ingredient_created",
  "catalogue.ingredient_updated",
  "catalogue.recipe_version_created",
  "brand.updated",
  "shift.opened",
  "shift.cash_movement_recorded",
  "shift.cash_movement_reviewed",
  "shift.close_requested",
  "shift.closed",
  "sale.completed",
  "sale.marketplace_recorded",
  "sale.awaiting_payment",
  "payment.qris_requested",
  "payment.qris_captured",
  "payment.qris_closed",
  "payment.webhook_rejected",
  "payment.account_registered",
  "payment.qris_overridden",
  "payment.exception_opened",
  "payment.exception_resolved",
  "payment.account_created",
  "payment.account_updated",
  "payment.method_configured",
  "payment.manual_qris_configured",
  "payment.manual_qris_recorded",
  "payment.manual_qris_verified",
  "payment.manual_qris_rejected",
  "payment.manual_qris_duplicate",
  "refund.requested",
  "refund.authorized",
  "refund.declined",
  "refund.succeeded",
  "refund.failed",
  "refund.unknown",
  "receipt.printed",
  "receipt.reprinted",
  "stock.opening_recorded",
  "stock.policy_changed",
  "stock.consumed",
  "stock.released",
  "stock.receipt_recorded",
  "stock.receipt_approved",
  "stock.receipt_rejected",
  "stock.count_submitted",
  "stock.count_approved",
  "stock.count_rejected",
  "stock.threshold_set",
  "order.draft_held",
  "order.table_moved",
  "order.drafts_merged",
  "order.draft_discarded",
  "customer.created",
  "customer.updated",
  "customer.consent_recorded",
  "customer.merged",
  "customer.duplicate_dismissed",
  "customer.marketing_exported",
  "report.exported",
  "discount.created",
  "tax_rule.created",
  "tax_rule.updated",
  "discount.updated",
  "discount.policy_updated",
  "discount.approval_requested",
  "discount.approval_approved",
  "discount.approval_rejected",
  "discount.approval_consumed",
] as const;
export type AuditAction = (typeof AUDIT_ACTIONS)[number];

/** Weekday keys of outlet opening hours, Monday first. */
export const WEEKDAYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"] as const;
export type Weekday = (typeof WEEKDAYS)[number];

/** Timezones a new outlet can start in: Indonesia's WIB, WITA and WIT (#92). */
export const OUTLET_TIMEZONES = ["Asia/Jakarta", "Asia/Makassar", "Asia/Jayapura"] as const;
export type OutletTimezone = (typeof OUTLET_TIMEZONES)[number];

/** An outlet code once upper-cased: 2 to 16 letters, digits or dashes (#92). */
export const OUTLET_CODE_PATTERN = /^[A-Z0-9-]{2,16}$/;

/** What a product is (CAT-01). Bundles arrive with their allocation rules in a later ticket. */
export const PRODUCT_KINDS = ["beverage", "food", "retail"] as const;
export type ProductKind = (typeof PRODUCT_KINDS)[number];

/**
 * What a unit measures (R1-13). Each has one base unit (g, ml, pcs) that ingredients are counted in;
 * quantities never convert across dimensions.
 */
export const UNIT_DIMENSIONS = ["mass", "volume", "count"] as const;
export type UnitDimension = (typeof UNIT_DIMENSIONS)[number];

/** Order channels a product can be sold on. External marketplaces are captured separately (CHN-01). */
export const SALES_CHANNELS = ["dine_in", "takeaway", "delivery"] as const;
export type SalesChannel = (typeof SALES_CHANNELS)[number];

/** Platforms a register device can run on (PRD: Web SPA, Tauri Android/iOS, optional desktop). */
export const DEVICE_PLATFORMS = ["web", "android", "ios", "desktop"] as const;
export type DevicePlatform = (typeof DEVICE_PLATFORMS)[number];

/** Square WebP thumbnail edge lengths (px) stored for a product photo (R1-10). */
export const PRODUCT_IMAGE_SIZES = [160, 320] as const;
export type ProductImageSize = (typeof PRODUCT_IMAGE_SIZES)[number];

/** Square WebP edge lengths (px) stored for a tenant's brand logo (R1-09c): sidebar mark and lockup. */
export const BRAND_LOGO_SIZES = [64, 256] as const;
export type BrandLogoSize = (typeof BRAND_LOGO_SIZES)[number];

/** The brand every tenant shows until its owner sets a name (R1-09c, `docs/brand.md`). */
export const DEFAULT_BRAND_NAME = "Urban Coffee";

/**
 * A register shift (CASH-01): `open` takes cash movements; `closing` holds the cashier's blind count
 * until a manager approves the close; `closed` is final.
 */
export const SHIFT_STATUSES = ["open", "closing", "closed"] as const;
export type ShiftStatus = (typeof SHIFT_STATUSES)[number];

/** Cash put into or taken out of the drawer during a shift; a `drop` goes to the safe. */
export const CASH_MOVEMENT_KINDS = ["paid_in", "paid_out", "drop"] as const;
export type CashMovementKind = (typeof CASH_MOVEMENT_KINDS)[number];

/** Only approved movements count towards expected cash. */
export const CASH_MOVEMENT_STATUSES = ["pending", "approved", "rejected"] as const;
export type CashMovementStatus = (typeof CASH_MOVEMENT_STATUSES)[number];

/**
 * Order lifecycle (R1-16): an online cash sale completes as it commits. A QRIS sale (R1-24) commits
 * `awaiting_payment` — priced, stock reserved, no receipt number yet — and completes once the gateway
 * confirms. That is the only status change an order makes; voids and refunds are compensating records
 * of later slices (PRD invariant 7), never a status edit that erases history.
 */
export const ORDER_STATUSES = ["completed", "awaiting_payment"] as const;
export type OrderStatus = (typeof ORDER_STATUSES)[number];

/**
 * A held order before payment (R1-19): `open` is editable and resumable from any register at its
 * outlet; it ends `completed` (its sale committed under the same order ID), `merged` (its lines moved
 * into another draft) or `discarded`. Only open drafts change; a paid order is never a draft again.
 */
export const DRAFT_STATUSES = ["open", "merged", "completed", "discarded"] as const;
export type DraftStatus = (typeof DRAFT_STATUSES)[number];

/**
 * Lifecycle events an order's append-only trail records: `completed` arrives with the sale itself, or
 * — for a QRIS sale (R1-24) — after `awaiting_payment`, once the gateway confirms the payment.
 */
export const ORDER_EVENT_TYPES = ["completed", "awaiting_payment"] as const;
export type OrderEventType = (typeof ORDER_EVENT_TYPES)[number];

/**
 * How an order was paid: cash at the drawer, QRIS through a payment gateway (R1-24), or — only during
 * a gateway outage — the merchant's own printed static QRIS, verified by hand (R1-28, PRD PAY-03).
 */
export const PAYMENT_METHODS = ["cash", "qris", "manual_qris_static"] as const;
export type PaymentMethod = (typeof PAYMENT_METHODS)[number];

/**
 * Cash is captured at the drawer as it is taken. A QRIS attempt (R1-24) starts `pending` and ends
 * once: `captured` when the gateway confirms it, or `failed`, `expired` or `cancelled`. A manual
 * static-QRIS payment (R1-28) starts `awaiting_verification` and ends once: `captured` when authorized
 * staff verify it against the merchant account, or `rejected`. A captured attempt never changes again.
 */
export const PAYMENT_ATTEMPT_STATUSES = [
  "captured",
  "pending",
  "failed",
  "expired",
  "cancelled",
  "awaiting_verification",
  "rejected",
] as const;
export type PaymentAttemptStatus = (typeof PAYMENT_ATTEMPT_STATUSES)[number];

/** A manual static-QRIS payment's statuses (R1-28), as the verification queue filters them. */
export const MANUAL_QRIS_STATUSES = ["awaiting_verification", "captured", "rejected"] as const;
export type ManualQrisStatus = (typeof MANUAL_QRIS_STATUSES)[number];

/**
 * What a verifier recorded about a manual static-QRIS payment (R1-28, AC-36): `verified` (found in
 * the merchant account: the order is paid), `rejected` (not found), or `duplicate` — its merchant
 * reference already verified another payment, so it went to review instead of crediting twice.
 */
export const MANUAL_QRIS_OUTCOMES = ["verified", "rejected", "duplicate"] as const;
export type ManualQrisOutcome = (typeof MANUAL_QRIS_OUTCOMES)[number];

/**
 * Payment gateway providers behind the `PaymentGateway` port (PRD ADR-10). `fake` is the
 * deterministic test and demo gateway: selectable only with PAYMENT_FAKE_GATEWAY_ENABLED=true, never
 * in production, and labelled SIMULASI wherever the register shows it.
 */
export const PAYMENT_PROVIDERS = ["fake", "sumopod"] as const;
export type PaymentProvider = (typeof PAYMENT_PROVIDERS)[number];

/** A gateway account (merchant) of a legal entity; only an active one takes new attempts. */
export const GATEWAY_ACCOUNT_STATUSES = ["active", "disabled"] as const;
export type GatewayAccountStatus = (typeof GATEWAY_ACCOUNT_STATUSES)[number];

/**
 * What processing a verified payment webhook did (R1-24, AC-07). Only `captured` and `applied`
 * change the attempt; the rest are recorded as evidence and change nothing:
 * - `duplicate`: the attempt already has this outcome (a redelivery under a new event ID).
 * - `stale`: older than the event that set the attempt's status, or after its capture.
 * - `late_success`: a capture for an attempt that already expired, failed or was cancelled — a
 *   reconciliation exception for R1-26 (AC-08), never a silent capture.
 * - `rejected_*`: the webhook didn't match the attempt's merchant, amount or currency.
 * - `unknown_reference`: no attempt of that account has the gateway reference.
 * - `noted`: a status that changes nothing (e.g. `pending`, `unknown`).
 */
export const PAYMENT_WEBHOOK_OUTCOMES = [
  "captured",
  "applied",
  "duplicate",
  "stale",
  "late_success",
  "rejected_merchant",
  "rejected_amount",
  "rejected_currency",
  "unknown_reference",
  "noted",
] as const;
export type PaymentWebhookOutcome = (typeof PAYMENT_WEBHOOK_OUTCOMES)[number];

/**
 * Payment methods a manager configures (R1-27, PRD PAY-01). Cash is always taken; QRIS is enabled or
 * disabled per legal entity, with an optional per-outlet override, and routed to a gateway account.
 * `manual_qris_static` (R1-28) is the outage fallback: off unless enabled, and only with the merchant's
 * registered static QRIS on the legal entity.
 */
export const CONFIGURABLE_PAYMENT_METHODS = ["qris", "manual_qris_static"] as const;
export type ConfigurablePaymentMethod = (typeof CONFIGURABLE_PAYMENT_METHODS)[number];

/** Gateway operations whose outcome feeds an account's health (R1-27). */
export const GATEWAY_OPERATIONS = ["create", "inquiry", "refund"] as const;
export type GatewayOperation = (typeof GATEWAY_OPERATIONS)[number];

/**
 * A gateway account's health (R1-27, PRD §9): `unhealthy` from its recent call failures — the
 * register then shows "Gateway tidak tersedia" — `degraded` when webhooks lag, `unknown` before any
 * recent traffic. Failures age out of the window, so a recovered gateway becomes usable again.
 */
export const GATEWAY_HEALTH_STATUSES = ["healthy", "degraded", "unhealthy", "unknown"] as const;
export type GatewayHealthStatus = (typeof GATEWAY_HEALTH_STATUSES)[number];

/** Where a stored gateway fact came from: a webhook, or the worker's status inquiry (R1-26). */
export const PAYMENT_EVENT_SOURCES = ["webhook", "inquiry"] as const;
export type PaymentEventSource = (typeof PAYMENT_EVENT_SOURCES)[number];

/**
 * A QRIS reconciliation case (R1-26, PRD PAY-03): funds the gateway confirmed that no payment posted.
 * - `late_success`: captured after its QR expired, failed or was cancelled, while the order still
 *   awaits payment (AC-08). Resolved by accepting it as the order's payment, or a manual refund.
 * - `overpayment`: captured for an order already paid otherwise — e.g. cash taken under a manager
 *   override (AC-37). The customer is owed the amount; resolved by a recorded manual refund.
 * - `duplicate_reference` (R1-28, AC-36): a manual static-QRIS payment was verified with a merchant
 *   reference that already paid another order. Nothing is credited: a reviewer accepts it with its
 *   own (corrected) reference, or rejects it.
 */
export const PAYMENT_EXCEPTION_KINDS = ["late_success", "overpayment", "duplicate_reference"] as const;
export type PaymentExceptionKind = (typeof PAYMENT_EXCEPTION_KINDS)[number];

export const PAYMENT_EXCEPTION_STATUSES = ["open", "resolved"] as const;
export type PaymentExceptionStatus = (typeof PAYMENT_EXCEPTION_STATUSES)[number];

/**
 * How a case was resolved: the funds paid the order, were refunded by hand (PRD PAY-05), or — a
 * duplicate manual reference only — the payment wasn't found and was rejected.
 */
export const PAYMENT_EXCEPTION_RESOLUTIONS = ["accepted", "refunded", "rejected"] as const;
export type PaymentExceptionResolution = (typeof PAYMENT_EXCEPTION_RESOLUTIONS)[number];

/**
 * An offline reconciliation case (R1-41) is `open` until a manager decides it. `resolving` is the
 * accepted sale being recorded: a reject can't race it, and a retried accept resumes it.
 */
/**
 * Why a stored offline sale was quarantined (R1-41): its authority or leased evidence at push time, or
 * the sale commit refusing it (a strict stock shortage, an approved shift close).
 */
export const SYNC_QUARANTINE_REASONS = [
  "lease_invalid",
  "employee_not_found",
  "employee_not_allowed",
  /** The named cashier never signed in on this register during the lease: attribution unproven. */
  "employee_unverified",
  "offline_evidence_invalid",
  "offline_quantity_cap",
  "offline_value_cap",
  "insufficient_stock",
  "shift_not_open",
] as const;
export type SyncQuarantineReason = (typeof SYNC_QUARANTINE_REASONS)[number];

export const SYNC_EXCEPTION_STATUSES = ["open", "resolving", "resolved"] as const;
export type SyncExceptionStatus = (typeof SYNC_EXCEPTION_STATUSES)[number];

/**
 * `accepted`: the sale is recorded as the cashier rang it up, on the manager's authority.
 * `rejected`: no sale is recorded; the command and its cash evidence stay on file.
 */
export const SYNC_EXCEPTION_RESOLUTIONS = ["accepted", "rejected"] as const;
export type SyncExceptionResolution = (typeof SYNC_EXCEPTION_RESOLUTIONS)[number];

export const DISCOUNT_SCOPES = ["line", "order"] as const;
export type DiscountScope = (typeof DISCOUNT_SCOPES)[number];

export const DISCOUNT_KINDS = ["fixed", "percent"] as const;
export type DiscountKind = (typeof DISCOUNT_KINDS)[number];

/** Expired is derived for an approved request at/after its deadline, never persisted. */
export const DISCOUNT_APPROVAL_STATUSES = ["pending", "rejected", "approved", "consumed", "expired"] as const;
export type DiscountApprovalStatus = (typeof DISCOUNT_APPROVAL_STATUSES)[number];

export const DISCOUNT_ERROR_CODES = [
  "discount_selection_invalid",
  "discount_not_eligible",
  "discount_revision_conflict",
  "discount_policy_revision_conflict",
  "discount_quote_required",
  "discount_quote_stale",
  "discount_approval_not_required",
  "discount_approval_required",
  "discount_approval_pending",
  "discount_approval_rejected",
  "discount_approval_expired",
  "discount_approval_consumed",
  "discount_approval_mismatch",
  "discount_self_approval",
  "discount_approval_revision_conflict",
  "discount_approval_not_pending",
  "discount_approval_request_conflict",
  "discount_state_busy",
] as const;
export type DiscountErrorCode = (typeof DISCOUNT_ERROR_CODES)[number];

/**
 * Why a customer's data may be used beyond the sale itself (CRM-01): marketing is recorded separately
 * from transaction processing, as an append-only ledger whose latest entry is in force.
 */
export const CONSENT_PURPOSES = ["marketing"] as const;
export type ConsentPurpose = (typeof CONSENT_PURPOSES)[number];

/** Where a consent was given: at the register while selling, or in the back office. */
export const CONSENT_SOURCES = ["register", "back_office"] as const;
export type ConsentSource = (typeof CONSENT_SOURCES)[number];

/** A customer stays `active` until a manager merges it into another record (never deleted). */
export const CUSTOMER_STATUSES = ["active", "merged"] as const;
export type CustomerStatus = (typeof CUSTOMER_STATUSES)[number];

/** What makes two customers likely the same person, for the duplicate review. */
export const DUPLICATE_REASONS = ["phone", "email", "name"] as const;
export type DuplicateReason = (typeof DUPLICATE_REASONS)[number];

/**
 * Delivery platforms whose orders the cashier records by hand (CHN-01, R1-21). The platform is the
 * order's external channel, separate from its order type (dine-in / takeaway / delivery).
 */
export const MARKETPLACE_PROVIDERS = ["grabfood", "gofood", "shopeefood"] as const;
export type MarketplaceProvider = (typeof MARKETPLACE_PROVIDERS)[number];

/** Where an order came from: the register itself, or a delivery platform (recorded manually in R1). */
export const ORDER_SOURCES = ["pos", ...MARKETPLACE_PROVIDERS] as const;
export type OrderSource = (typeof ORDER_SOURCES)[number];

/** What a payment-clearing entry stands for; a marketplace receivable awaits the platform's payout. */
export const CLEARING_KINDS = ["marketplace_receivable"] as const;
export type ClearingKind = (typeof CLEARING_KINDS)[number];

/** A clearing entry stays `open` until a settlement (a later slice) matches it. */
export const CLEARING_STATUSES = ["open"] as const;
export type ClearingStatus = (typeof CLEARING_STATUSES)[number];

/** How an outlet's stock location treats a sale that needs more than is available (R1-22). */
export const STOCK_POLICIES = ["track", "strict"] as const;
export type StockPolicy = (typeof STOCK_POLICIES)[number];

/** Append-only stock movements (INV-01); receipts, counts, waste and transfers join in later slices. */
export const STOCK_MOVEMENT_KINDS = [
  "opening",
  "reserve",
  "consume",
  "release",
  "receive",
  "count_adjustment",
  "waste",
  "return",
] as const;
export type StockMovementKind = (typeof STOCK_MOVEMENT_KINDS)[number];

/** An order's reservation of one ingredient: active until preparation consumes or a cancel releases it. */
export const STOCK_RESERVATION_STATUSES = ["active", "consumed", "released"] as const;
export type StockReservationStatus = (typeof STOCK_RESERVATION_STATUSES)[number];

/**
 * A manual stock receipt or a basic count (R1-23): recorded by staff, it changes stock only once a
 * manager approves it; a rejection keeps it on record without moving stock.
 */
export const STOCK_DOCUMENT_STATUSES = ["pending", "approved", "rejected"] as const;
export type StockDocumentStatus = (typeof STOCK_DOCUMENT_STATUSES)[number];

/**
 * A refund's lifecycle (R1-29, PRD PAY-05). `requested` reserves nothing; `authorized`, `processing`
 * and `unknown` reserve the order's refundable balance; `succeeded` has paid it out; `failed` (a
 * declined request, or a gateway's definitive refusal) releases it. A timeout is `unknown`, never
 * `failed`: the balance stays reserved until the gateway confirms either way (AC-38).
 */
export const REFUND_STATUSES = [
  "requested",
  "authorized",
  "processing",
  "succeeded",
  "failed",
  "unknown",
] as const;
export type RefundStatus = (typeof REFUND_STATUSES)[number];

/** Statuses that hold the order's refundable balance (PAY-05). */
export const RESERVING_REFUND_STATUSES = ["authorized", "processing", "unknown", "succeeded"] as const;

/**
 * How a refund is paid: from the register's drawer, by a recorded manual transfer to the customer,
 * or through the original payment's gateway — only where its adapter offers refunds.
 */
export const REFUND_METHODS = ["cash", "manual_transfer", "gateway"] as const;
export type RefundMethod = (typeof REFUND_METHODS)[number];

/**
 * What happens to a refunded line's stock (PRD invariant 7, INV-02): `waste` — prepared, its
 * consumption is reclassified as waste, never returned (AC-09); `restock` — an unopened retail item,
 * inspected, back in stock; `none` — no stock effect.
 */
export const REFUND_DISPOSITIONS = ["waste", "restock", "none"] as const;
export type RefundDisposition = (typeof REFUND_DISPOSITIONS)[number];

/** Thermal paper widths a receipt is laid out for (PRD PRINT-01): 32 and 48 characters a line. */
export const PAPER_WIDTHS = ["58", "80"] as const;
export type PaperWidth = (typeof PAPER_WIDTHS)[number];

/**
 * How a receipt is printed (R1-31): the browser's print dialog (or its "Save as PDF"), a PDF the
 * server renders and stores, or the device's silent ESC/POS print to its LAN thermal printer (#37).
 */
export const PRINT_CHANNELS = ["browser", "pdf", "native"] as const;
export type PrintChannel = (typeof PRINT_CHANNELS)[number];

/**
 * A print job's lifecycle (PRD PRINT-02): `queued` until the device starts, `sending` while it hands
 * the document over, then `sent` (handed to the printer or stored — not proof of paper), `failed`, or
 * `outcome_unknown` when the device can't tell. The last three are final; trying again is a reprint.
 */
export const PRINT_JOB_STATUSES = ["queued", "sending", "sent", "failed", "outcome_unknown"] as const;
export type PrintJobStatus = (typeof PRINT_JOB_STATUSES)[number];

/** The first print of an order is the original; every later one is a labelled, audited copy. */
export const PRINT_JOB_KINDS = ["original", "reprint"] as const;
export type PrintJobKind = (typeof PRINT_JOB_KINDS)[number];

/**
 * Where a product is prepared (R1-32, PRD §14): its kitchen/bar tickets go to that station's queue.
 * A product with no station (retail, by default) is handed over as is and makes no ticket.
 */
export const PREPARATION_STATIONS = ["bar", "kitchen"] as const;
export type PreparationStation = (typeof PREPARATION_STATIONS)[number];

/** A new product's station when none is given: drinks at the bar, food in the kitchen, retail none. */
export function defaultStation(kind: ProductKind): PreparationStation | null {
  return kind === "beverage" ? "bar" : kind === "food" ? "kitchen" : null;
}

/**
 * Fulfillment tickets (R1-32): an `order` ticket per order and station; a `change` ticket when items
 * come off an order before they are ready (a refund), and a `cancel` ticket when the order is
 * cancelled before preparation. Change and cancel tickets are distinct events, never edits.
 */
export const FULFILLMENT_TICKET_KINDS = ["order", "change", "cancel"] as const;
export type FulfillmentTicketKind = (typeof FULFILLMENT_TICKET_KINDS)[number];

/**
 * An order ticket goes `queued` → `in_progress` (preparation started: stock consumed) → `ready`, or
 * `queued` → `cancelled`. Change and cancel tickets go `queued` → `acknowledged`.
 */
export const FULFILLMENT_TICKET_STATUSES = [
  "queued",
  "in_progress",
  "ready",
  "cancelled",
  "acknowledged",
] as const;
export type FulfillmentTicketStatus = (typeof FULFILLMENT_TICKET_STATUSES)[number];

/** Statuses a station still works on: the kitchen queue. The rest are done (left the queue). */
export const ACTIVE_TICKET_STATUSES = [
  "queued",
  "in_progress",
] as const satisfies readonly FulfillmentTicketStatus[];
/** Statuses of tickets that left the queue: the kitchen's "Selesai" list. */
export const DONE_TICKET_STATUSES = [
  "ready",
  "cancelled",
  "acknowledged",
] as const satisfies readonly FulfillmentTicketStatus[];

/**
 * The moves a station makes (`POST /v1/kitchen-tickets/:id/<move>`): the tickets each fits — an order
 * ticket, or an amendment (a change or cancel ticket) — and the status it goes from and to. The
 * server enforces them; the kitchen screen offers the one that fits a ticket.
 */
export const TICKET_MOVES = {
  start: { kind: "order", from: "queued", to: "in_progress" },
  ready: { kind: "order", from: "in_progress", to: "ready" },
  acknowledge: { kind: "amendment", from: "queued", to: "acknowledged" },
} as const satisfies Record<
  string,
  { kind: "order" | "amendment"; from: FulfillmentTicketStatus; to: FulfillmentTicketStatus }
>;
export type TicketMove = keyof typeof TICKET_MOVES;

/** The move that takes a ticket on from where it is; none once it left the queue. */
export function nextTicketMove(ticket: {
  kind: FulfillmentTicketKind;
  status: FulfillmentTicketStatus;
}): TicketMove | undefined {
  const kind = ticket.kind === "order" ? "order" : "amendment";
  return (Object.keys(TICKET_MOVES) as TicketMove[]).find(
    (move) => TICKET_MOVES[move].kind === kind && TICKET_MOVES[move].from === ticket.status,
  );
}
